import React, { useEffect, useMemo, useState } from "react";

import { Button, Header, Loader } from "semantic-ui-react";

import SnackMessage from "../common/SnackMessage";
import FacebookLoginButton from "../IsHealth/Common/FacebookLogin/FBLoginButton";
import GoogleLoginButton from "../IsHealth/Common/GoogleLogin/GoogleLoginButton";
import LineLoginButton from "../IsHealth/Common/Line/LoginButton";
import ModConfirmOA from "../IsHealth/REG/ModConfirmOA";

import {
  ACTIONS,
  BTN_ACTS,
  ConnectionActionType,
  ConnectionProviderType,
  PickedState,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/AccountInformation";

import CONFIG from "config/config";

import "./CardAccountInformation.scss";

// Types
type CardAccountInformationProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  AccountInformationSequence: State["AccountInformationSequence"];
  deviceId?: string;
  loginWithSocial?: ConnectionProviderType;
  userEmail?: string;
};

const IMAGES = {
  apple: "",
  email: "/static/images/landing/mail-circular-icon.png",
  facebook: "/static/images/landing/facebook-circular-icon.png",
  google: "/static/images/landing/google-circular-icon.png",
  line: "/static/images/landing/line-circular-icon.png",
} as const;

const ACTION_LABEL = {
  connect: "Connect",
  default: "Default Account",
  disconnect: "Disconnected",
};

const CardAccountInformation = (props: CardAccountInformationProps) => {
  const [openModDelete, setOpenModDelete] = useState<boolean>(false);
  const [modDisconnect, setModDisconnect] = useState<number | null>(null);
  const [connectLoading, setConnectLoading] = useState<
    Partial<Record<ConnectionProviderType, boolean>>
  >({});

  useEffect(() => {
    props.runSequence({ sequence: "AccountInformation", restart: true });

    return () => {
      props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
    };
  }, []);

  const socialAccounts = useMemo(() => {
    const available = props.AccountInformationSequence?.connectionAvailable ?? [];
    const items = props.AccountInformationSequence?.connectionItems ?? [];

    const defaultProvider = items[0]?.provider;

    const formattedItems = available.map((item) => {
      const foundConnect = items.find((acc) => acc.provider === item.provider);

      let action: ConnectionActionType = "connect";

      if (defaultProvider === item.provider) {
        action = "default";
      } else if (foundConnect) {
        action = "disconnect";
      }

      return {
        ...item,
        action,
        id: foundConnect?.id,
        icon: IMAGES[item.provider],
        status: foundConnect ? "Connected" : "Not Connected",
      };
    });

    return formattedItems.sort((item) => (item.action === "default" ? -1 : 0));
  }, [
    props.AccountInformationSequence?.connectionAvailable,
    props.AccountInformationSequence?.connectionItems,
  ]);

  const handleDeleteAccount = (): void => {
    setOpenModDelete(true);
  };

  const handleCloseModConfirm = () => {
    setOpenModDelete(false);
    setModDisconnect(null);
  };

  const handleConfirmDeleteAccount = () => {
    props.runSequence({
      sequence: "AccountInformation",
      action: ACTIONS.DELETE_ACCOUNT,
      onSuccess: handleCloseModConfirm,
    });
  };

  const handleResponseFacebook = async (result: any) => {
    await props.runSequence({
      sequence: "AccountInformation",
      action: ACTIONS.FACEBOOK_CONNECT,
      ...result,
    });
  };

  const handleResponseGoogle = async (data: any) => {
    setConnectLoading({ google: true });

    await props.runSequence({
      sequence: "AccountInformation",
      action: ACTIONS.GOOGLE_CONNECT,
      ...data,
    });

    setConnectLoading({ google: false });
  };

  const handleResponseLine = async (data: any) => {
    setConnectLoading({ line: true });

    await props.runSequence({
      sequence: "AccountInformation",
      action: ACTIONS.LINE_CONNECT,
      ...data,
    });

    setConnectLoading({ line: false });
  };

  const handleDisconnect = (data: { id?: number }) => () => {
    if (data.id) {
      setModDisconnect(data.id);
    }
  };

  const handleConfirmDisconnect = async () => {
    if (modDisconnect) {
      props.runSequence({
        sequence: "AccountInformation",
        action: ACTIONS.DISCONNECT,
        id: modDisconnect,
        onSuccess: handleCloseModConfirm,
      });
    }
  };

  console.log("AccountInformation props:", props, socialAccounts);

  return (
    <div className="account-information">
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      {/* Account Information Section */}
      <div className="section account-section">
        <Header className="section-header" as="h3">
          Account Information
        </Header>

        <div className="account-row">
          <span className="account-label">Your log-in account</span>
          <div className="account-info">
            {props.loginWithSocial && (
              <img alt="login-with-social" src={IMAGES[props.loginWithSocial]} />
            )}
            <span>{props.userEmail || "-"}</span>
          </div>
        </div>
      </div>

      {/* Social Account Section */}
      <div className="section social-section">
        <Header className="section-header" as="h3">
          Social Account
        </Header>

        <div className="social-accounts">
          {socialAccounts.map((account) => (
            <div key={account.provider} className="social-account-row">
              <div className="social-account-info">
                <img alt="account.icon" src={account.icon} />
                <span className="social-account-name">{account.name}</span>
              </div>

              <span
                className={`status-text ${
                  account.status === "Connected" ? "connected" : "not-connected"
                }`}
              >
                {account.status}
              </span>

              <div className={`social-account-actions ${account.action}`}>
                {account.provider === "line" && (
                  <LineLoginButton
                    disabled={account.action === "default"}
                    lineLoginText={ACTION_LABEL[account.action]}
                    shimmer
                    onCallback={account.action === "connect" ? handleResponseLine : undefined}
                    onClick={handleDisconnect(account)}
                    textStyle={{
                      fontSize: "14px",
                      margin: "auto",
                    }}
                  />
                )}
                {account.provider === "facebook" && (
                  <FacebookLoginButton
                    appId="1782946359024414"
                    deviceId={props.deviceId}
                    disabled={account.action === "default"}
                    fbLoginText={ACTION_LABEL[account.action]}
                    version="v23.0"
                    shimmer
                    onCallback={handleResponseFacebook}
                    onClick={
                      account.action === "disconnect" ? handleDisconnect(account) : undefined
                    }
                    onLoading={(isLoading) => {
                      setConnectLoading({ [account.provider]: isLoading });
                    }}
                  />
                )}
                {account.provider === "google" && (
                  <GoogleLoginButton
                    boxStyle={{ height: "auto" }}
                    clientId={CONFIG.GOOGLE_CLIENT_ID}
                    disabled={account.action === "default"}
                    googleLoginText={ACTION_LABEL[account.action]}
                    shimmer
                    onCallback={handleResponseGoogle}
                    onClick={
                      account.action === "disconnect" ? handleDisconnect(account) : undefined
                    }
                  />
                )}
                {connectLoading[account.provider] && <Loader size="mini" active />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button className="delete-button" size="small" basic onClick={handleDeleteAccount}>
        Delete Account
      </Button>

      <ModConfirmOA
        approveButtonPosition="right"
        approveButtonText="ยืนยัน"
        denyButtonText="ยกเลิก"
        isLoading={!!props.buttonLoadCheck?.[BTN_ACTS.DELETE_ACCOUNT]}
        open={openModDelete}
        subtitle="คุณแน่ใจหรือไม่ว่าต้องการลบบัญชีนี้"
        title="ยืนยันการลบ"
        onApprove={handleConfirmDeleteAccount}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
      />

      <ModConfirmOA
        approveButtonPosition="right"
        approveButtonText="ยืนยัน"
        denyButtonText="ยกเลิก"
        isLoading={!!props.buttonLoadCheck?.[BTN_ACTS.DISCONNECT]}
        open={!!modDisconnect}
        subtitle="การเชื่อมต่อบัญชีหรือไม่ ?"
        title="ต้องการยกเลิก"
        onApprove={handleConfirmDisconnect}
        onClose={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
      />
    </div>
  );
};

CardAccountInformation.displayName = "AccountInformation";

export default React.memo(CardAccountInformation);
